/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, RotateCcw, Upload } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  REFERENCE_AUDIT_MISSED_TEXT,
  REFERENCE_CHANGED_TEXT,
  REFERENCE_HINT,
  REFERENCE_SLOTS,
  REFERENCE_UNREADABLE_TEXT,
  SLOT_LABELS,
  checkReferencePng,
  referenceImageUrl,
  replaceBody,
  replacedText,
  rolledBackText,
  shortSha,
  sizeText,
  sourceText,
  unchangedText,
  type ReferenceSlot,
  type ReferenceView,
} from '../lib/mascotReference';
import { Button, Callout, Icon, Panel, Skeleton, StatusChip } from '../ui';
import { SaveNote } from './SaveNote';

export interface CharacterReferenceSectionProps {
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
}

type Read =
  | { status: 'loading' }
  /** A service without the route (an older one): the section is not shown. */
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: ReferenceView };

/**
 * "Character reference" on the outfit gallery page (owner, 2026-10-31: the identity reference Gyro supplies with every
 * render ask is hosted by the service; an admin replaces a slot here, previous kept one step back). Each slot shows its
 * current image, hash, size and provenance, a Replace that reads a PNG, checks its header against the slot's rules and
 * sends it with the revision loaded, and a Roll back while a previous image is kept. The section reads its own view and
 * hides on a service without it.
 */
export function CharacterReferenceSection({ locked }: CharacterReferenceSectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [busy, setBusy] = useState<ReferenceSlot | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const inputs = useRef<Partial<Record<ReferenceSlot, HTMLInputElement | null>>>({});

  const show = useCallback((view: ReferenceView) => setRead({ status: 'loaded', view }), []);

  const load = useCallback(async () => {
    try {
      const view = await api.desktopMascotReference();
      if (view === null) setRead({ status: 'absent' });
      else show(view);
    } catch (error) {
      setRead({ status: 'failed', text: errorText(error, REFERENCE_UNREADABLE_TEXT) });
    }
  }, [show]);

  useEffect(() => {
    let live = true;
    api.desktopMascotReference().then(
      (view) => {
        if (!live) return;
        if (view === null) setRead({ status: 'absent' });
        else show(view);
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: errorText(error, REFERENCE_UNREADABLE_TEXT) });
      },
    );
    return () => {
      live = false;
    };
  }, [show]);

  if (read.status === 'absent') return null;
  const view = read.status === 'loaded' ? read.view : null;
  const disabled = locked || busy !== null || view === null;

  /** A change's answer: the view, with the note that fits it. */
  const settle = (slot: ReferenceSlot, answer: ReferenceView, done: (slot: ReferenceSlot) => string) => {
    show(answer);
    if (answer.auditMissed) setNote({ ok: false, text: `${done(slot)} ${REFERENCE_AUDIT_MISSED_TEXT}` });
    else setNote({ ok: true, text: answer.changed === false ? unchangedText(slot) : done(slot) });
  };
  const refused = async (error: unknown) => {
    const code = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error : undefined;
    if (code === 'mascot_reference_changed') {
      setNote({ ok: false, text: REFERENCE_CHANGED_TEXT });
      await load();
    } else {
      setNote({ ok: false, text: errorText(error, 'Nothing was changed.') });
    }
  };

  const replace = async (slot: ReferenceSlot, file: File) => {
    if (!view) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const checked = checkReferencePng(slot, bytes);
    if (!checked.ok) {
      setNote({ ok: false, text: `${SLOT_LABELS[slot].title}: ${checked.message} Nothing was sent.` });
      return;
    }
    setBusy(slot);
    setNote(null);
    try {
      settle(slot, await api.replaceDesktopMascotReference(slot, replaceBody(bytes, view.revision)), replacedText);
    } catch (error) {
      await refused(error);
    } finally {
      setBusy(null);
    }
  };

  const rollback = async (slot: ReferenceSlot) => {
    if (!view) return;
    setBusy(slot);
    setNote(null);
    try {
      settle(slot, await api.rollbackDesktopMascotReference(slot, { expectedRevision: view.revision }), rolledBackText);
    } catch (error) {
      await refused(error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel
      heading="h3"
      titleId="mascot-reference-title"
      title={<span className="ui-title-icon"><Icon icon={ImageIcon} size="md" />Character reference</span>}
      description={REFERENCE_HINT}
    >
      {read.status === 'loading' && <div className="ui-form" aria-hidden="true"><Skeleton size="md" width="240px" /><Skeleton size="lg" /></div>}
      {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
      {view && (
        <ul className="ui-people-list" aria-label="Character reference slots">
          {REFERENCE_SLOTS.map((slot) => {
            const entry = view.slots[slot];
            const record = entry.record;
            return (
              <li key={slot} data-slot={slot} data-sha256={record?.sha256 ?? ''} data-source={record ? (entry.isSeed ? 'seed' : record.source) : 'none'}>
                <div className="ui-actions">
                  {record ? (
                    <img src={referenceImageUrl(slot, record.sha256)} alt={`${SLOT_LABELS[slot].title}, current image`} className="ui-tile-img" />
                  ) : null}
                  <div>
                    <div className="ui-actions">
                      <strong>{SLOT_LABELS[slot].title}</strong>
                      {record && <StatusChip tone={entry.isSeed ? 'neutral' : 'ok'}>{entry.isSeed ? 'Shipped seed' : 'Replaced'}</StatusChip>}
                    </div>
                    <p className="ui-cell-sub">{SLOT_LABELS[slot].hint}</p>
                    <p className="ui-cell-sub" data-source-line>
                      {record ? `${sizeText(record)}, sha256 ${shortSha(record.sha256)}. ` : ''}{sourceText(entry)}
                      {record?.previous ? ` Previous: sha256 ${shortSha(record.previous.sha256)} (${record.previous.source === 'seed' ? 'the shipped seed' : record.previous.setBy ?? 'an admin'}).` : ''}
                    </p>
                  </div>
                </div>
                <div className="ui-actions">
                  <input
                    ref={(element) => { inputs.current[slot] = element; }}
                    type="file"
                    accept="image/png"
                    aria-label={`Choose a PNG for ${SLOT_LABELS[slot].title}`}
                    hidden
                    disabled={disabled}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void replace(slot, file);
                    }}
                  />
                  <Button size="sm" icon={Upload} busy={busy === slot} disabled={disabled} onClick={() => inputs.current[slot]?.click()}>Replace…</Button>
                  <Button variant="ghost" size="sm" icon={RotateCcw} disabled={disabled || !record?.previous} onClick={() => void rollback(slot)}>Roll back</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <SaveNote note={note} />
    </Panel>
  );
}
